import React, { forwardRef, useState, useMemo } from 'react';
import {
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  Box,
  Checkbox,
  Card,
  useTheme,
  IconButton,
  InputLabel,
  FormHelperText,
  FormGroup,
} from '@material-ui/core';
import { DatePicker } from '@material-ui/lab';
import { Controller } from 'react-hook-form';
import ImageUploading from 'react-images-uploading';
import ClearIcon from '@material-ui/icons/Clear';
import dayjs from 'dayjs';
import SvgIconML from '@/components/SvgIconML/SvgIconML';
import Cascade from '@/components/cascader_ml/CascaderMl';
import AntdTree from '@/components/AntdTree/AntdTree';
import { uploadFile } from '@/utils/uploadImg';
import ClearIconML from '@/components/ClearIconML/index';
import { fmtThousand } from '@/utils/comUtil';
import FormTypeStyled from './FormTypeStyles';

const initErrorStyle = {
  color: '#f44336',
  margin: 0,
  fontSize: '0.75rem',
  marginTop: '-8px',
  textAlign: 'left',
  fontWeight: '400',
  lineHeight: '1.66',
  letterSpacing: '0.03333em',
};

/**
 * @description: 文本输入框组件
 * @param {autoFocus} If true, the input element is focused during the first mount.
 * @param {initLabel} 自定义的label是否显示
 * @param {showLabel} 是否需要显示label
 * @param {helperText} 用于校验的提醒文字
 * @param {label}
 * @param {name} 维护输入数据的关键字
 * @param {errors} 表单的错误集合
 * @param {isNumber} 输入数字
 * @param {isPositiveNumber} true 只能输入正数，false可以输入负数
 * @param {isParseInt} true 只能输入正整数，false可以输入小数
 * @param {thousands} true 开启千分位
 * @param {showError} false输入框捕获到错误不展示错误样式，默认true
 * @param {initErrorText} 自定义必填校验文案
 * @param {rules} 校验规则的集合
 * @param {style} 自定义样式
 * @param {multiline} true开启多行文本输入，默认false不开启
 * @param {maxRows} 开启多行后的最大行数
 * @param {minRows} 开启多行后的最小行数
 * @param {variant} 控制输入框的样式，standard、outlined两种，默认standard
 * @param {handleInitEvent} 自定义的默认事件，用来处理额外的事件
 * @param {isValidate} 自定义校验，主要用于正则校验不通过的提醒
 * @param {isAdditionalRequired} 必填项的前置条件
 * @return {component}
 */

export const TextFieldCom = forwardRef(
  (
    {
      control,
      label,
      showLabel = true,
      isPositiveNumber = false,
      endAdornment = null,
      isParseInt = false,
      initLabel,
      name,
      rules,
      errors,
      setValue,
      textClassName,
      style,
      isNumber,
      showError = true,
      setFieldValue,
      parameter,
      realName,
      multiline = false,
      maxRows = 4,
      minRows = 4,
      variant = 'standard',
      initErrorText,
      disabled,
      defaultValue = '',
      handleInitEvent,
      clearArrayData,
      getValues,
      isValidate,
      thousands = false,
      isAdditionalRequired = false,
      ...props
    },
    ref,
  ) => {
    return (
      <FormTypeStyled>
        <Box
          className={
            isNumber ? 'form-component-wrap' : multiline ? 'multiline-wrap' : ''
          }
        >
          {initLabel && (
            <Box
              sx={{
                fontSize: 12,
                color: '#212100',
                marginBottom: '8px',
              }}
            >
              {rules?.required && <span style={{ color: 'red' }}>*</span>}
              {label}
            </Box>
          )}
          <Controller
            render={(pr) => (
              <TextField
                multiline={multiline}
                maxRows={maxRows}
                minRows={minRows}
                sx={{ width: '100%', ...style }}
                ref={ref}
                variant={variant}
                autoComplete="off"
                label={
                  showLabel &&
                  label && (
                    <>
                      {rules?.required && (
                        <span style={{ color: 'red' }}>*</span>
                      )}{' '}
                      {label}
                    </>
                  )
                }
                {...props}
                className={textClassName}
                onChange={(e) => {
                  let v = e.target.value;
                  if (isNumber) {
                    const f = v.charAt(0);
                    if (f === '-') {
                      const n = v.substr(1, v.length);
                      if (isNaN(n) && !thousands) {
                        v = '';
                      }
                    } else if (isNaN(v) && !thousands) {
                      v = '';
                    }
                    if (isPositiveNumber) {
                      v = v.replace(/-/g, '');
                    }
                    if (isParseInt) {
                      // eslint-disable-next-line radix
                      v = v.replace(/\D/g, '');
                    }
                    if (thousands) {
                      const n = v ? v.replace(/,/g, '') : '';
                      v = v ? fmtThousand(Number(n)) : '';
                    }
                  }
                  setFieldValue &&
                    setFieldValue({
                      ...parameter,
                      value: v,
                      realName,
                    });
                  handleInitEvent && handleInitEvent(v);
                  return pr.onChange(v);
                }}
                onBlur={(e) => {
                  let v = e.target.value;
                  pr.onChange(v ? v.trim() : '');
                }}
                error={showError && errors && Boolean(errors[name])}
                helperText={errors[name]?.message}
                disabled={disabled}
                value={pr.value ?? ''}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        className="clear-btn"
                        onClick={() => {
                          setValue(name, '');
                          clearArrayData &&
                            clearArrayData({
                              ...parameter,
                              value: '',
                            });
                        }}
                        edge="end"
                        disabled={disabled}
                      >
                        {pr.value || pr.value === 0 ? (
                          <ClearIcon className="clear-icon" />
                        ) : (
                          ''
                        )}
                      </IconButton>
                      {endAdornment && (
                        <span
                          style={{
                            fontSize: '12px',
                            color: '#404041',
                            marginLeft: '4px',
                          }}
                        >
                          {endAdornment}
                        </span>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            )}
            control={control}
            name={name}
            rules={{
              validate: () => {
                if (!rules || JSON.stringify(rules) === '{}') {
                  return true;
                }
                let errMes = '';
                const val = getValues(name) === 0 ? '0' : getValues(name);
                const n = val || val === '0' ? val.replace(/,/g, '') : '';
                console.log(name, val);
                for (let v in rules) {
                  if (
                    !isAdditionalRequired &&
                    v === 'required' &&
                    rules[v] &&
                    val === ''
                  ) {
                    errMes = initErrorText || `${label}是必填项`;
                  } else if (v === 'maxLength' && rules[v] < val?.length) {
                    errMes = `输入不超过${rules[v]}个文字`;
                  } else if (v === 'pattern') {
                    if (rules[v].test(getValues(name))) {
                      errMes = isValidate;
                    }
                  } else if (
                    v === 'max' &&
                    rules[v] &&
                    val !== '' &&
                    n > rules[v]
                  ) {
                    errMes = `最大不超过${rules[v]}`;
                  } else if (
                    v === 'min' &&
                    rules[v] &&
                    val !== '' &&
                    n < rules[v]
                  ) {
                    errMes = `最小不小于${rules[v]}`;
                  }
                }
                if (errMes) {
                  return errMes;
                }
                return true;
              },
            }}
            defaultValue={defaultValue}
          />
        </Box>
      </FormTypeStyled>
    );
  },
);

export const SelectFieldCom = forwardRef(
  (
    {
      typeList = [],
      label,
      disabled,
      initLabel,
      control,
      name,
      rules,
      errors,
      setValue,
      watchAllFields,
      fieldNames = { label: 'name', value: 'value' },
      placeholder,
      noNeed = false,
      ...props
    },
    ref,
  ) => {
    return (
      <Box flex="1">
        {initLabel && (
          <Box sx={{ fontSize: 12, color: '#212100', marginBottom: '12px' }}>
            <span style={{ color: 'red' }}>*</span>
            {label}
          </Box>
        )}
        <Box className="display-row">
          <FormControl variant="standard" sx={{ width: '100%' }}>
            <InputLabel
              id={`demo-simple-select-${name}-label`}
              error={errors && Boolean(errors[name])}
            >
              {rules?.required && <span style={{ color: 'red' }}>*</span>}
              {label}
            </InputLabel>
            <Controller
              render={(pr) => (
                <Select
                  labelId={`demo-simple-select-${name}-label`}
                  id={`demo-simple-select-${name}`}
                  displayEmpty
                  sx={{ width: '100%' }}
                  ref={ref}
                  {...props}
                  disabled={disabled}
                  onChange={(e) => {
                    return pr.onChange(e.target.value);
                  }}
                  error={errors && Boolean(errors[name])}
                  value={pr.value ?? ''}
                  label={label ? label : ''}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <span
                          style={{ color: 'currentColor', opacity: '0.42' }}
                        >
                          {placeholder}
                        </span>
                      );
                    }
                    if (
                      typeList &&
                      Array.isArray(typeList) &&
                      typeList.length > 0
                    ) {
                      const s = typeList.find(
                        (item) => item[`${fieldNames.value}`] === selected,
                      );
                      return s ? s[`${fieldNames.label}`] : '';
                    }
                    return '';
                  }}
                >
                  <MenuItem disabled value="">
                    {placeholder}
                  </MenuItem>
                  {typeList.map((item) => (
                    <MenuItem
                      key={item[`${fieldNames.value}`]}
                      value={item[`${fieldNames.value}`]}
                    >
                      {item[`${fieldNames.label}`]}
                    </MenuItem>
                  ))}
                </Select>
              )}
              label=""
              name={name}
              control={control}
              rules={rules}
            />
            {Boolean(errors[name]) && (
              <FormHelperText
                error={Boolean(errors[name])}
              >{`${label}是必选项`}</FormHelperText>
            )}
            <ClearIconML
              handleClick={() => {
                setValue(name, '');
              }}
              clearValue={!noNeed && !disabled && watchAllFields[name]}
            ></ClearIconML>
          </FormControl>
        </Box>
      </Box>
    );
  },
);

export const RadioFieldCom = forwardRef(
  (
    {
      queryType,
      control,
      typeList = [],
      lWidth,
      children,
      row = true,
      name,
      initLabel,
      label,
      defaultValue = '',
      disabled,
      wrapStyle,
      labelStyle,
      radioStyle,
      ...props
    },
    ref,
  ) => {
    return (
      <Box sx={wrapStyle}>
        {initLabel && (
          <Box
            sx={{
              fontSize: 16,
              color: '#212121',
              marginBottom: '12px',
              ...labelStyle,
            }}
          >
            <span style={{ color: 'red' }}>*</span>
            {label}
          </Box>
        )}
        <Box className="display-row">
          <Controller
            render={(pr) => (
              <RadioGroup
                aria-label="gender"
                {...props}
                {...pr}
                value={pr.value ?? defaultValue}
                ref={ref}
                onChange={(e) => {
                  return pr.onChange(e.target.value);
                }}
                row={row}
                sx={{ gap: '0 48px', ...radioStyle }}
                className="checkbox-radio-disabled-color"
              >
                {typeList.map((item, index) => (
                  <Box>
                    <FormControlLabel
                      key={index}
                      sx={{
                        minWidth: lWidth ? lWidth : '102px',
                      }}
                      disabled={
                        queryType === 'detail' || item.disabled || disabled
                      }
                      value={item.value}
                      control={<Radio />}
                      label={
                        queryType === 'detail' || item.disabled ? (
                          item.label
                        ) : (
                          <span style={{ color: '#212121' }}>{item.label}</span>
                        )
                      }
                    />
                    {Array.isArray(children) &&
                      children.length > 0 &&
                      children[index]}
                  </Box>
                ))}
              </RadioGroup>
            )}
            name={name}
            control={control}
            defaultValue={defaultValue}
          />
        </Box>
      </Box>
    );
  },
);

export const CheckBoxFieldCom = forwardRef(
  (
    {
      onCallBack,
      // defaultValue = false,
      name,
      control,
      disabled,
      checkedBoxList = [],
      getValues,
      setValue,
      style,
      children,
    },
    ref,
  ) => {
    return (
      <Controller
        render={(pr) => (
          <FormGroup
            disabled={disabled}
            className="formGroup"
            ref={ref}
            sx={style}
          >
            {checkedBoxList.map((item) => (
              <Box
                sx={{ display: 'flex' }}
                className="checkbox-radio-disabled-color"
              >
                <FormControlLabel
                  variant="standard"
                  value={item.value}
                  disabled={disabled}
                  checked={Boolean(getValues(name)?.includes(item.value))} //value 如果是数字，在传入的时候先转成字符串后使用
                  control={<Checkbox color="primary" />}
                  label={<Box color="#000">{item.label}</Box>}
                  onChange={(e) => {
                    const checkedArr = getValues(name) || [];
                    if (checkedArr.includes(e.target.value)) {
                      const arr = checkedArr.filter(
                        (i) => i !== e.target.value,
                      );
                      setValue(name, arr.length > 0 ? arr : '');
                      onCallBack && onCallBack(arr);
                      return pr.onChange(arr.length > 0 ? arr : '');
                    }
                    setValue(name, [...checkedArr, e.target.value]);
                    onCallBack && onCallBack([...checkedArr, e.target.value]);
                    return pr.onChange([...checkedArr, e.target.value]);
                  }}
                />
                {item.isRightShow ? children : null}
              </Box>
            ))}
          </FormGroup>
        )}
        name={name}
        control={control}
        // defaultValue={defaultValue}
      />
    );
  },
);

/**
 * @description: 文本输入框组件
 * @param {multiple} 开启一次上传多张
 * @param {uW} 上传图片的宽
 * @param {iW} 展示图片的宽
 * @param {iH} 展示图片的高
 * @param {maxNumber} 最大上传数量
 * @param {label} label
 * @param {required} 是否必须上传
 * @param {tips} 底部的提醒文案
 * @param {tipsAlign} 底部的提醒文案的位置
 * @param {handlePreview} 预览
 * @param {disabled} 是否禁用
 * @param {acceptType} 允许上传的格式
 * @param {defaultValues} 默认图片
 * @param {showSuccessNumber} 开启展示已上传成功数量
 * @return {component}
 */
export const UploadImageCom = forwardRef(
  (
    {
      multiple = true,
      uW = '120',
      uH = '120',
      iW = '120',
      iH = 'auto',
      maxNumber = 10,
      pr,
      label,
      required = false,
      tips,
      tipsAlign = 'left',
      handlePreview,
      disabled,
      acceptType = ['png', 'jpg', 'jpeg', 'webp'],
      defaultValues,
      showSuccessNumber,
    },
    ref,
  ) => {
    const theme = useTheme();
    const [images, setImages] = useState([]);
    const [fetchUrls, setFetchUrl] = useState([]);
    const onChange = (imageList) => {
      let list = [];
      /*获取未传给后台的图片*/
      const imgList =
        maxNumber > 1
          ? [...imageList].splice(images.length, maxNumber)
          : imageList;
      const filesPromise = imgList.map((fileItem, index) => {
        return new Promise((resolve) => {
          const formData = new FormData();
          formData.append('file', fileItem.file);
          formData.append('bucket', 'ACTIVITY-BOOKING-FILE');
          formData.append('isPublic', true);
          formData.append('isFixedName', true);
          uploadFile('/common/upload', formData, (res) => {
            if (res.code === 0) {
              list.push(fileItem);
              resolve(res.data.url);
            }
            if (res.code === '-999') {
              resolve('上传失败');
              console.log('上传失败的图片是', imgList[index].file.name);
            }
          });
        });
      });
      Promise.all(filesPromise)
        .then((url) => {
          const successUrl = url.filter((item) => item !== '上传失败');
          const newUrl =
            maxNumber > 1 ? [...fetchUrls, ...successUrl] : successUrl;
          pr.onChange(newUrl);
          setFetchUrl(newUrl);
        })
        .then(() => {
          setImages(maxNumber > 1 ? [...images, ...list] : list);
        });
    };

    const onImageDelete = (index) => {
      let imgs = [...images];
      let urls = [...fetchUrls];
      imgs.splice(index, 1);
      urls.splice(index, 1);
      setImages(imgs);
      pr.onChange(urls);
      setFetchUrl(urls);
      if (urls.length === 0) {
        pr.onChange();
        return true;
      }
    };
    useMemo(() => {
      if (defaultValues) {
        const defaultDataUrl = defaultValues.map((item) => ({
          data_url: `${process.env.FILE_URL}/${item}`,
        }));
        setImages(defaultDataUrl);
        setFetchUrl(defaultValues);
      }
    }, [defaultValues]);
    return (
      <FormTypeStyled>
        <Box ref={ref} sx={{ width: '100%' }}>
          {label && (
            <Box mb={2} color="#212100" fontSize="12px">
              {required && <span style={{ color: 'red' }}>*</span>} {label}{' '}
              <span style={{ color: '#999999' }}>
                {showSuccessNumber &&
                  `，已上传(${fetchUrls.length}/${maxNumber})`}
              </span>
            </Box>
          )}
          <ImageUploading
            multiple={multiple}
            value={images}
            onChange={onChange}
            maxNumber={maxNumber}
            dataURLKey="data_url"
            acceptType={acceptType}
          >
            {({
              // imageList,
              onImageUpload,
              // onImageRemoveAll,
              onImageUpdate,
              // onImageRemove,
              // isDragging,
              dragProps,
            }) => (
              // write your building UI
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  gap: '25px 49px',
                  flexWrap: 'wrap',
                }}
              >
                {images.map((image, index) => (
                  <Box
                    key={index}
                    className="upload-image-item"
                    sx={{
                      width: `${iW}px`,
                      height: `${iH}px`,
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: '4px',
                      border: '1px solid #cccccc',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src={image['data_url']}
                      alt=""
                      style={{ maxWidth: '100%', maxHeight: '100%' }}
                    />
                    <Card className="montmorillonite-layer-wrap"></Card>
                    <Card className="montmorillonite-layer-in">
                      <Box className="icon-wrap">
                        <IconButton
                          className="icon-button"
                          aria-label="edit"
                          onClick={() => handlePreview(image['data_url'])}
                          //  onClick={onEdit}
                        >
                          <SvgIconML
                            name="imgPreview"
                            size={24}
                            fill={theme.palette.primary.main}
                          />
                        </IconButton>
                      </Box>
                      {!disabled && (
                        <Box className="icon-wrap">
                          {/* <UpdateIcon
                        size={24}
                          className="icon-style"
                          onClick={() => onImageUpdate(index)}
                        /> */}
                          <IconButton
                            className="icon-button"
                            aria-label="edit"
                            onClick={() =>
                              maxNumber > 1
                                ? onImageDelete(index)
                                : onImageUpdate(index)
                            }
                          >
                            <SvgIconML
                              name={
                                maxNumber > 1 ? 'circleDelete' : 'updateImg'
                              }
                              size={24}
                              fill="#ffffff"
                            />
                          </IconButton>
                        </Box>
                      )}
                    </Card>
                  </Box>
                ))}
                {!disabled && images.length < maxNumber && (
                  <Box
                    sx={{
                      width: `${uW}px`,
                      height: `${uH}px`,
                      background: '#F5F5F5',
                      borderRadius: '4px',
                      border: '1px solid #CCCCCC',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onClick={onImageUpload}
                    {...dragProps}
                  >
                    {/* <img src={AddPhoto} alt="" width="38" /> */}
                    {/* <AddPhoto sx={{ width: '38px', height: '38px' }} /> */}
                    <SvgIconML
                      name="add_photo_alternate"
                      size={38}
                      className="preview"
                    />
                  </Box>
                )}
              </Box>
            )}
          </ImageUploading>
          {tips && !disabled && (
            <Box mt={0.5} color="#999999" fontSize="12px" textAlign={tipsAlign}>
              {tips}
            </Box>
          )}
        </Box>
      </FormTypeStyled>
    );
  },
);

export const CascadeFieldCom = forwardRef(
  (
    {
      label,
      setValue,
      name,
      realName,
      queryType,
      cascadeData,
      control,
      rules,
      showLabel,
      fieldNames,
      errors,
      setError,
      clearErrors,
      nameMessage,
      setFieldValue,
      parameter,
      isShowSearch,
      showError = true,
    },
    ref,
  ) => {
    const handleChangePca = (value, selectedItem) => {
      setValue(
        name,
        selectedItem && Array.isArray(selectedItem) && selectedItem.length > 0
          ? selectedItem
          : '',
      );
      if (
        !selectedItem ||
        (selectedItem &&
          Array.isArray(selectedItem) &&
          selectedItem.length === 0)
      ) {
        setFieldValue && setFieldValue({ ...parameter, value: [], realName });
        setError(name, {
          type: 'required',
        });
      } else {
        setFieldValue &&
          setFieldValue({ ...parameter, value: selectedItem, realName });
        clearErrors([name]);
      }
    };
    return (
      <Box ref={ref} width="200px">
        {showLabel && (
          <Box
            sx={{
              fontSize: 12,
              color: '#212100',
              marginBottom: '-8px',
            }}
          >
            {rules?.required && <span style={{ color: 'red' }}>*</span>}
            {label}
          </Box>
        )}
        <Controller
          name={name}
          control={control}
          as={
            <Cascade
              label={label}
              cascadeData={cascadeData}
              disabled={queryType === 'detail'}
              handleChangePca={handleChangePca}
              fieldNames={fieldNames}
              isShowSearch={isShowSearch}
              initStatus={true}
            />
          }
          rules={rules}
        />
        {showError && errors[name] && (
          <Box sx={{ ...initErrorStyle, marginTop: '8px' }}>
            {errors[name] ? nameMessage : ''}
          </Box>
        )}
      </Box>
    );
  },
);

export const AntdTreeFieldCom = forwardRef(
  (
    {
      showLabel,
      rules,
      label,
      name,
      control,
      queryType,
      fieldNames,
      treeData,
      setValue,
      errors,
      setError,
      clearErrors,
      nameMessage,
      placeholder,
    },
    ref,
  ) => {
    const selectValueSet = (keys) => {
      setValue(name, keys.length > 0 ? keys[0] : '');
      if (!keys || (keys && Array.isArray(keys) && keys.length === 0)) {
        setError(name, {
          type: 'required',
        });
      } else {
        clearErrors([name]);
      }
    };
    return (
      <Box ref={ref}>
        {showLabel && (
          <Box
            sx={{
              fontSize: 12,
              color: '#212100',
              marginBottom: '8px',
            }}
          >
            {rules?.required && <span style={{ color: 'red' }}>*</span>}
            {label}
          </Box>
        )}
        <Controller
          name={name}
          control={control}
          as={
            <AntdTree
              treeData={treeData}
              fieldNames={fieldNames}
              disabled={queryType === 'detail'}
              selectValueSet={selectValueSet}
              placeholder={placeholder}
              errors={errors}
            />
          }
          rules={rules}
        />
        {errors[name] && (
          <Box sx={{ ...initErrorStyle, marginTop: 0 }}>
            {errors[name] ? nameMessage : ''}
          </Box>
        )}
      </Box>
    );
  },
);

export const DatePickerCom = forwardRef(
  (
    {
      showLabel,
      rules,
      label,
      name,
      control,
      disabled,
      errors,
      nameMessage,
      showError = true,
      defaultValue = null,
      maxDate = null,
      minDate = null,
    },
    ref,
  ) => {
    // console.log()
    return (
      <Box ref={ref}>
        {showLabel && (
          <Box
            sx={{
              fontSize: 12,
              color: '#212100',
              marginBottom: '8px',
            }}
          >
            {rules?.required && <span style={{ color: 'red' }}>*</span>}
            {label}
          </Box>
        )}
        <Controller
          name={name}
          control={control}
          as={(pr) => {
            return (
              <DatePicker
                label={label}
                inputFormat="yyyy-MM-dd" //时间格式化
                value={pr.value ?? ''}
                maxDate={maxDate}
                minDate={minDate}
                disabled={disabled}
                onChange={(newValue) => {
                  const date = dayjs(newValue).format('YYYY-MM-DD');
                  return pr.onChange(date);
                }}
                renderInput={(params) => {
                  return (
                    <Box>
                      <TextField
                        sx={{
                          height: 'auto',
                          display: 'flex',
                          alignItems: 'flex-end',
                        }}
                        variant="standard"
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: '请选择时间',
                          readonly: 'readonly',
                        }}
                      />
                    </Box>
                  );
                }}
              />
            );
          }}
          defaultValue={defaultValue}
          rules={rules}
        />
        {showError && errors[name] && (
          <Box sx={initErrorStyle}>{errors[name] ? nameMessage : ''}</Box>
        )}
      </Box>
    );
  },
);
